import React, { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import {
  PackageCheck,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Loader2,
} from 'lucide-react'
import { apiGet, apiGetWithMeta } from '../lib/api'
import { StorefrontLayout } from '../layouts/StorefrontLayout'
import { OrderTimeline } from '../components/OrderTimeline'

interface TimelineEvent {
  status: string
  message: string
  created_at: string
}

interface CustomerOrder {
  id: string
  orderNumber: string
  status: string
  customerName: string
  trackingId: string
  shippingCity: string
  shippingState: string
  total: number
  createdAt: string
  timeline?: TimelineEvent[]
}

export const CustomerOrdersPage: React.FC = () => {
  const [orders, setOrders] = useState<CustomerOrder[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      setError('')
      try {
        const res = await apiGetWithMeta<CustomerOrder[]>('orders', { page: 1, pageSize: 20 })
        const list = res.data ?? []
        setOrders(list)

        const withTimeline = await Promise.all(
          list.map(async (order) => {
            try {
              const detail = await apiGet<CustomerOrder & { timeline: TimelineEvent[] }>(`orders/${order.id}`)
              return { ...order, timeline: detail.timeline ?? [] }
            } catch {
              return order
            }
          }),
        )
        setOrders(withTimeline)
      } catch (err: unknown) {
        setError((err as Error).message || 'Could not load your orders.')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const formatCurrency = (amt: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amt)

  return (
    <StorefrontLayout>
      <div className="max-w-2xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--gold-main)] mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to store
        </Link>

        <div className="glass-luxury-card p-6 sm:p-8 space-y-6">
          <div className="flex items-center gap-2">
            <PackageCheck className="w-6 h-6 text-[var(--gold-main)]" />
            <h1 className="text-xl font-title font-bold">My Orders</h1>
          </div>

          {loading && (
            <div className="flex justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-[var(--gold-main)]" />
            </div>
          )}

          {error && (
            <div className="alert alert-error flex items-center gap-2 text-xs">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          {!loading && !error && orders.length === 0 && (
            <div className="text-center py-8 space-y-3">
              <p className="text-sm text-[var(--text-muted)]">You haven&apos;t placed any orders yet.</p>
              <Link to="/" className="btn-gold text-xs py-2.5 px-5 inline-block">
                Start shopping
              </Link>
            </div>
          )}

          {orders.length > 0 && (
            <div className="space-y-3">
              {orders.map((order) => (
                <div key={order.id} className="rounded-xl border border-[var(--border)] overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setExpandedId(expandedId === order.id ? null : order.id)}
                    className="w-full flex items-center justify-between p-4 text-left text-xs bg-[var(--bg-surface-2)] hover:bg-[var(--bg-surface-3)] transition"
                  >
                    <div>
                      <span className="font-mono font-bold text-[var(--gold-main)]">{order.orderNumber}</span>
                      <span className={`badge badge-${order.status} ml-2`}>{order.status}</span>
                      <p className="text-[var(--text-muted)] mt-1">
                        {new Date(order.createdAt).toLocaleDateString('en-IN')} · {formatCurrency(order.total)}
                      </p>
                    </div>
                    {expandedId === order.id ? (
                      <ChevronUp className="w-4 h-4 text-[var(--text-muted)]" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-[var(--text-muted)]" />
                    )}
                  </button>
                  {expandedId === order.id && (
                    <div className="p-4 border-t border-[var(--border)]">
                      <OrderTimeline
                        data={{
                          order_number: order.orderNumber,
                          status: order.status,
                          customer_name: order.customerName,
                          tracking_id: order.trackingId,
                          shipping_city: order.shippingCity,
                          shipping_state: order.shippingState,
                          total: order.total,
                          created_at: order.createdAt,
                          timeline: order.timeline ?? [],
                        }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <p className="text-[10px] text-[var(--text-faint)] text-center">
            Have a tracking ID?{' '}
            <Link to="/track" className="text-[var(--gold-main)] hover:underline">
              Track a single shipment
            </Link>
          </p>
        </div>
      </div>
    </StorefrontLayout>
  )
}
